import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  checkAuthSession,
  clearFreshLogin,
  clearSessionEndedMarker,
  clearSsoAuthPending,
  isFreshLogin,
  markSsoAuthPending,
  postLoginTarget,
  pinAuthEmail,
} from "../../utils/authSession";

/** Microsoft auth codes are single-use. Never exchange the same code twice. */
const exchangedCodes = new Set();
let inFlightCode = "";

function readCookie(name) {
  if (typeof document === "undefined" || !document.cookie) return null;
  const prefix = `${name}=`;
  const parts = document.cookie.split(";");
  for (const raw of parts) {
    const item = raw.trim();
    if (item.startsWith(prefix)) {
      return decodeURIComponent(item.substring(prefix.length));
    }
  }
  return null;
}

function clearCookie(name) {
  try {
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
  } catch (e) {
    // ignore
  }
}

function goToApp() {
  clearSessionEndedMarker();
  clearFreshLogin();
  try {
    const returnTo = (sessionStorage.getItem("reachout_return_to") || "").trim();
    sessionStorage.removeItem("reachout_return_to");
    if (returnTo.startsWith("/sso/")) {
      window.location.replace(returnTo);
      return;
    }
  } catch {
    // ignore
  }
  postLoginTarget().then((path) => window.location.replace(path));
}

async function enterIfSessionExists() {
  for (let i = 0; i < 8; i++) {
    const authed = await checkAuthSession({ ignoreEndedMarker: true });
    if (authed) {
      goToApp();
      return true;
    }
    await new Promise((r) => setTimeout(r, 250));
  }
  return false;
}

const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const refresh = params.get("refresh");
    const code = params.get("code");
    const error = params.get("error");

    if (token) {
      markSsoAuthPending();
      clearSessionEndedMarker();
      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      goToApp();
      return;
    }

    if (code) {
      markSsoAuthPending();
      const inflightKey = `oauth_pkce_${code}`;

      if (sessionStorage.getItem(inflightKey) === "pending" || inFlightCode === code) {
        return;
      }
      if (sessionStorage.getItem(inflightKey) === "done" || exchangedCodes.has(code)) {
        void enterIfSessionExists();
        return;
      }

      const state = params.get("state");
      const pkceKey = state ? `oauth_pkce_v1_${state}` : null;
      let codeVerifier = sessionStorage.getItem("msal_pkce_verifier");
      if (!codeVerifier && pkceKey) {
        codeVerifier = localStorage.getItem(pkceKey);
      }
      if (!codeVerifier && state) {
        codeVerifier = readCookie(`oauth_pkce_v1_${encodeURIComponent(state)}`) || readCookie(`oauth_pkce_v1_${state}`);
      }

      const storedRedirect = sessionStorage.getItem("msal_redirect_uri");
      const redirectUri =
        storedRedirect && storedRedirect.startsWith(`${window.location.origin}`)
          ? storedRedirect
          : `${window.location.origin}/auth/callback`;

      if (!codeVerifier) {
        console.warn(
          "[SSO] Missing PKCE verifier after Microsoft redirect. This happens when the browser clears " +
          "sessionStorage/localStorage during the Microsoft login redirect (strict tracking prevention, " +
          "Safari ITP, or private mode). Redirecting to login to retry."
        );
        navigate("/?sso_retry=1", { replace: true });
        return;
      }

      inFlightCode = code;
      exchangedCodes.add(code);
      sessionStorage.setItem(inflightKey, "pending");

      const clearPkceSecrets = () => {
        if (pkceKey) {
          localStorage.removeItem(pkceKey);
        }
        sessionStorage.removeItem("msal_pkce_verifier");
        sessionStorage.removeItem("msal_redirect_uri");
        sessionStorage.removeItem("msal_oauth_state");
        if (state) {
          clearCookie(`oauth_pkce_v1_${encodeURIComponent(state)}`);
          clearCookie(`oauth_pkce_v1_${state}`);
        }
      };

      (async () => {
        // Always same-origin so Set-Cookie lands on the UI host (localhost:3003), not :8082.
        const exchangeUrl = `${window.location.origin}/api/auth/exchange-token`;

        let exchangeRes = null;
        let lastErr = null;
        try {
          exchangeRes = await fetch(exchangeUrl, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ code, codeVerifier, redirectUri }),
          });
        } catch (e) {
          lastErr = e;
        }

        if (exchangeRes && exchangeRes.ok) {
          const body = await exchangeRes.json().catch(() => ({}));
          if (body?.user?.email) {
            pinAuthEmail(body.user.email);
          }
          sessionStorage.setItem(inflightKey, "done");
          inFlightCode = "";
          clearPkceSecrets();
          localStorage.removeItem("token");
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          goToApp();
          return;
        }

        if (exchangeRes) {
          const body = await exchangeRes.json().catch(() => ({}));
          if (
            exchangeRes.status === 403 &&
            (body?.code === "NOT_TENANT_MEMBER" ||
              body?.code === "NOT_INVITED" ||
              /not been invited/i.test(String(body?.message || "")))
          ) {
            try {
              sessionStorage.setItem("blocked_user", JSON.stringify({
                email:     body.userEmail     || "",
                firstName: body.userFirstName || "",
                lastName:  body.userLastName  || "",
              }));
            } catch (_) { /* storage unavailable */ }
            sessionStorage.removeItem(inflightKey);
            inFlightCode = "";
            clearSsoAuthPending();
            clearPkceSecrets();
            navigate("/unauthorized", { replace: true });
            return;
          }
          console.warn("[SSO] Token exchange failed", exchangeRes.status, body?.message || body);
        } else {
          console.warn("[SSO] Token exchange network error", lastErr);
        }

        if (!isFreshLogin() && await enterIfSessionExists()) {
          sessionStorage.setItem(inflightKey, "done");
          inFlightCode = "";
          clearPkceSecrets();
          return;
        }

        sessionStorage.removeItem(inflightKey);
        inFlightCode = "";
        clearSsoAuthPending();
        clearPkceSecrets();
        localStorage.removeItem("token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        const failStatus = exchangeRes ? exchangeRes.status : 0;
        navigate(failStatus === 0 || failStatus >= 500 ? "/?server_starting=1" : "/?sso_retry=1", { replace: true });
      })();

      return;
    }

    if (inFlightCode) {
      return;
    }

    if (error) {
      clearSsoAuthPending();
      localStorage.removeItem("token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      navigate("/", { replace: true });
      return;
    }

    navigate("/", { replace: true });
  }, [location.search, navigate]);

  return (
    <div className="loading-page">
      <div className="loading-spinner spinner-border text-secondary" role="status" />
      <div className="loading-text">Signing you in...</div>
    </div>
  );
};

export default AuthCallback;
